import { AuthenticationService } from "../authenticationService";
import { APIService } from "../apiService";
import { Claim, S3SignedUrl, PaginatedClaims } from "src/types";
import { PostClaimRequestBody } from "src/types/api/claims/postClaim";
import { PostClaimImagesUrlRequestBody } from "src/types/api/claims/postClaimImagesUrls";
import { PostClaimImagesArchiveRequestBody } from "src/types/api/claims/postClaimImagesArchive";
import { GetClaimsQueryParams } from "src/types/api/claims/getClaims";
import { GetClaimsStatsResponse } from "src/types/api/claims/getClaimsStats";
import { PatchClaimRequestBody } from "src/types/api/claims/patchClaim";
import { PostClaimApproveRequestBody } from "src/types/api/claims/postClaimApprove";
import { PostClaimRejectRequestBody } from "src/types/api/claims/postClaimReject";
import { PostClaimConfirmRequestBody } from "src/types/api/claims/postClaimConfirm";
import { PostClaimCreditUrlsRequestBody } from "src/types/api/claims/postClaimCreditUrls";

class ClaimAPIService extends APIService {
  constructor(authenticationService: AuthenticationService) {
    super(authenticationService, "/v1/claims");
  }

  async getClaims(queryParams: GetClaimsQueryParams): Promise<PaginatedClaims> {
    return await this.jwtRequest("GET", "", undefined, queryParams);
  }

  async getClaimsStats(): Promise<GetClaimsStatsResponse> {
    return await this.jwtRequest("GET", "/stats");
  }

  async postClaim(requestBody: PostClaimRequestBody): Promise<Claim> {
    const path = "";
    return await this.jwtRequest("POST", path, requestBody);
  }

  async postClaimImagesUrls(claimId: string, requestBody: PostClaimImagesUrlRequestBody): Promise<S3SignedUrl[]> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}/image-urls`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postClaimImagesArchive(
    claimId: string,
    requestBody: PostClaimImagesArchiveRequestBody
  ): Promise<{ message: string }> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}/images/archive`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async getClaimById(claimId: string): Promise<Claim> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}`;
    return await this.jwtRequest("GET", path);
  }

  async patchClaim(claimId: string, requestBody: PatchClaimRequestBody): Promise<Claim> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}`;
    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async postClaimApprove(claimId: string, requestBody?: PostClaimApproveRequestBody): Promise<Claim> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}/approve`;
    return await this.jwtRequest("POST", path, requestBody);
  }

  async postClaimReject(claimId: string, requestBody: PostClaimRejectRequestBody): Promise<Claim> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}/reject`;
    return await this.jwtRequest("POST", path, requestBody);
  }

  async postClaimConfirm(claimId: string, requestBody?: PostClaimConfirmRequestBody): Promise<Claim> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}/confirm`;
    return await this.jwtRequest("POST", path, requestBody);
  }

  async postClaimCreditUrls(claimId: string, requestBody: PostClaimCreditUrlsRequestBody): Promise<S3SignedUrl[]> {
    const pathParam = encodeURIComponent(claimId);
    const path = `/${pathParam}/credit-urls`;
    return await this.jwtRequest("POST", path, requestBody);
  }
}

export { ClaimAPIService };
